import { Injectable } from '@angular/core';
import { AmlEntityType, CreateAmlEntry } from '@core/state/types/aml.types';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpAuthorizationHeader } from '../misc/httpauthorizationheader';
import { FirmActions } from '../state/firm/firm.actions';
import { BLService } from './bl.service';

@Injectable({ providedIn: 'root' })
export class AmlService extends BLService {
  private options = new HttpAuthorizationHeader('application/json');
  private RESOURCE_ENDPOINT = 'aml';
  private url = environment.serverUrlV2 + this.RESOURCE_ENDPOINT;

  getAmlApiEmail(): Observable<{ email: string | null }> {
    return this.http
      .get<{ email: string }>(`${this.url}/integration`, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  activateIntegration(data: { email: string; password: string }): Observable<void> {
    return this.http.post(`${this.url}/integration`, data, this.options.getAuthorizationHeader()).pipe(
      tap(({ email }) => this.store.dispatch(FirmActions.verifyAmlConnectionSucceeded({ email }))),
      this.catchErrorAndShowMessage(),
    );
  }

  updateActiveIntegrationCredentials(data: { email: string; password: string }): Observable<void> {
    return this.http.put(`${this.url}/integration`, data, this.options.getAuthorizationHeader()).pipe(
      tap(({ email }) => this.store.dispatch(FirmActions.verifyAmlConnectionSucceeded({ email }))),
      this.catchErrorAndShowMessage(),
    );
  }

  deactivateIntegration(): Observable<void> {
    return this.http.delete(`${this.url}/integration`, this.options.getAuthorizationHeader()).pipe(
      tap(() => this.store.dispatch(FirmActions.verifyAmlConnectionSucceeded({ email: null }))),
      this.catchErrorAndShowMessage(),
    );
  }

  createAmlForClient(clientId: number): Observable<AmlEntityType> {
    return this.http
      .post<AmlEntityType>(`${this.url}/${clientId}`, null, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  createManualAmlForClient(clientId: number, data: CreateAmlEntry): Observable<AmlEntityType> {
    return this.http
      .post<AmlEntityType>(`${this.url}/${clientId}/manual`, data, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  attachExistingAmlForClient(clientId: number, amlId: string): Observable<AmlEntityType> {
    return this.http
      .post<AmlEntityType>(`${this.url}/${clientId}/attach`, { amlId }, this.options.getAuthorizationHeader())
      .pipe(this.catchErrorAndShowMessage());
  }

  loadAmlDataForClient(clientId: number): Observable<AmlEntityType[]> {
    return this.http
      .get<AmlEntityType[]>(`${this.url}/${clientId}`, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }

  deleteAmlEntity(id: number): Observable<void> {
    return this.http
      .delete(`${this.url}/${id}`, this.options.getAuthorizationHeaderWithEmptyBody())
      .pipe(this.catchErrorAndShowMessage());
  }
}
